import { useCallback } from 'react';
import { useTurnstile } from '../providers/TurnstileProvider';

/*
 * useProtectedFetch()
 *
 * Returns a function with the same signature as `fetch()` but which:
 *   1. Obtains a fresh Turnstile token via useTurnstile().getToken()
 *   2. Attaches it as `X-Turnstile-Token` header
 *   3. Retries ONCE on 400/403 (likely expired/duplicate token) with a fresh token
 *
 * Usage:
 *   const protectedFetch = useProtectedFetch();
 *   const res = await protectedFetch(`${API}/api/academy/lookup-member/${n}`);
 */
export const useProtectedFetch = () => {
  const { getToken } = useTurnstile();

  return useCallback(
    async function protectedFetch(input, init = {}, _retried = false) {
      const token = await getToken();
      const headers = new Headers(init.headers || {});
      headers.set('X-Turnstile-Token', token);
      if (init.body && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
      const response = await fetch(input, { ...init, headers });
      // A 400 (missing/malformed) or 403 (failed check) after the first attempt
      // is usually an expired or duplicated token. Retry once with a fresh one.
      if (!_retried && (response.status === 400 || response.status === 403)) {
        try {
          const bodyText = await response.clone().text();
          if (/turnstile|bot check/i.test(bodyText)) {
            return protectedFetch(input, init, true);
          }
        } catch (_) {
          /* fall through */
        }
      }
      return response;
    },
    [getToken]
  );
};
